/**
 * Corgi Outfitters — progressive-enhancement JavaScript.
 *
 * Nothing here is required for navigation, browsing or the fundamental
 * add-to-cart path: those work through plain HTML forms/links/<details>.
 * This file only improves the experience when it can run.
 */

document.documentElement.setAttribute('data-js-enhanced', '');

/* -------------------------------------------------------------------- */
/* Cart count + add-to-cart enhancement                                  */
/* -------------------------------------------------------------------- */

function updateCartCount(count) {
  document.querySelectorAll('[data-cart-count]').forEach((el) => {
    el.textContent = String(count);
    el.hidden = count === 0;
  });
}

document.querySelectorAll('form[action*="/cart/add"], form.product__form').forEach((form) => {
  form.addEventListener('submit', async (event) => {
    if (!window.fetch) return; // let the native form submission happen

    event.preventDefault();
    const submitButton = form.querySelector('[data-add-to-cart]');
    const liveRegion = form.querySelector('[data-add-to-cart-live]');
    const textEl = form.querySelector('[data-add-to-cart-text]');
    const originalText = textEl ? textEl.textContent : '';

    if (submitButton) submitButton.setAttribute('aria-busy', 'true');

    try {
      const formData = new FormData(form);
      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { Accept: 'application/json' },
        body: formData,
      });

      if (!response.ok) {
        const error = await response.json().catch(() => null);
        throw new Error(error && error.description ? error.description : 'Unable to add to cart');
      }

      const cartResponse = await fetch('/cart.js', { headers: { Accept: 'application/json' } });
      const cart = await cartResponse.json();
      updateCartCount(cart.item_count);

      if (textEl) textEl.textContent = window.CorgiStrings?.added || 'Added';
      if (liveRegion) liveRegion.textContent = window.CorgiStrings?.added || 'Added to cart';

      setTimeout(() => {
        if (textEl) textEl.textContent = originalText;
      }, 2000);
    } catch (error) {
      if (liveRegion) liveRegion.textContent = error.message;
      form.submit(); // fall back to a full navigation add-to-cart
    } finally {
      if (submitButton) submitButton.removeAttribute('aria-busy');
    }
  });
});

/* -------------------------------------------------------------------- */
/* Variant selector                                                      */
/* -------------------------------------------------------------------- */

class VariantSelector extends HTMLElement {
  connectedCallback() {
    const json = this.querySelector('[data-variant-selector-json]');
    if (!json) return;

    try {
      this.variants = JSON.parse(json.textContent);
    } catch (error) {
      this.variants = [];
    }

    this.nativeSelect = this.querySelector('[data-variant-native-select]');
    this.inputs = Array.from(this.querySelectorAll('[data-variant-option-input]'));

    this.inputs.forEach((input) => {
      input.addEventListener('change', () => this.handleChange());
    });

    this.updateAvailability();
  }

  getSelectedOptions() {
    const fieldsets = Array.from(this.querySelectorAll('.variant-option'));
    return fieldsets.map((fieldset) => {
      const checked = fieldset.querySelector('[data-variant-option-input]:checked');
      return checked ? checked.value : null;
    });
  }

  findMatchingVariant(options) {
    return this.variants.find((variant) => variant.options.every((value, index) => value === options[index]));
  }

  handleChange() {
    const options = this.getSelectedOptions();

    this.querySelectorAll('.variant-option').forEach((fieldset) => {
      const checked = fieldset.querySelector('[data-variant-option-input]:checked');
      const selectedValueEl = fieldset.querySelector('[data-selected-value]');
      if (checked && selectedValueEl) selectedValueEl.textContent = checked.value;
    });

    const matched = this.findMatchingVariant(options);
    this.updateAvailability(options);

    if (!matched) return;

    if (this.nativeSelect) {
      this.nativeSelect.value = String(matched.id);
      this.nativeSelect.dispatchEvent(new Event('change', { bubbles: true }));
    }

    this.dispatchEvent(new CustomEvent('variant:change', { bubbles: true, detail: { variant: matched } }));
    this.updateProductForm(matched);
  }

  updateAvailability(currentOptions) {
    const options = currentOptions || this.getSelectedOptions();
    const fieldsets = Array.from(this.querySelectorAll('.variant-option'));

    fieldsets.forEach((fieldset, fieldsetIndex) => {
      const inputs = Array.from(fieldset.querySelectorAll('[data-variant-option-input]'));
      inputs.forEach((input) => {
        const testOptions = options.slice();
        testOptions[fieldsetIndex] = input.value;
        const hasAvailableMatch = this.variants.some(
          (variant) => variant.available && variant.options.every((value, index) => testOptions[index] == null || value === testOptions[index])
        );
        input.disabled = !hasAvailableMatch;
      });
    });
  }

  updateProductForm(variant) {
    const root = this.closest('.product') || document;
    const priceWrapper = root.querySelector('[data-product-price]');
    const addButton = root.querySelector('[data-add-to-cart]');
    const addText = root.querySelector('[data-add-to-cart-text]');

    if (priceWrapper && variant.price != null) {
      const money = (variant.price / 100).toLocaleString(document.documentElement.lang || 'en', {
        style: 'currency',
        currency: window.Shopify?.currency?.active || 'USD',
      });
      const regular = priceWrapper.querySelector('.co-price__regular');
      if (regular) regular.textContent = money;
    }

    if (addButton) {
      addButton.disabled = !variant.available;
      if (addText) {
        addText.textContent = variant.available
          ? window.CorgiStrings?.addToCart || 'Add to cart'
          : window.CorgiStrings?.soldOut || 'Sold out';
      }
    }
  }
}

if (!customElements.get('variant-selector')) {
  customElements.define('variant-selector', VariantSelector);
}

/* -------------------------------------------------------------------- */
/* Product media gallery — sync active thumbnail on scroll               */
/* -------------------------------------------------------------------- */

document.querySelectorAll('[data-product-media]').forEach((mediaBlock) => {
  const track = mediaBlock.querySelector('[data-media-track]');
  const thumbnails = Array.from(mediaBlock.querySelectorAll('[data-thumbnail]'));
  if (!track || thumbnails.length === 0 || !window.IntersectionObserver) return;

  const items = Array.from(track.querySelectorAll('[data-media-item]'));

  const observer = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        const index = items.indexOf(entry.target);
        thumbnails.forEach((thumb, thumbIndex) => {
          const isActive = thumbIndex === index;
          thumb.classList.toggle('is-active', isActive);
          thumb.setAttribute('aria-selected', String(isActive));
        });
      });
    },
    { root: track, threshold: 0.6 }
  );

  items.forEach((item) => observer.observe(item));
});

/* -------------------------------------------------------------------- */
/* Horizontal rails (bestsellers, complete the look)                     */
/* -------------------------------------------------------------------- */

document.querySelectorAll('.co-rail').forEach((rail) => {
  const track = rail.querySelector('[data-rail-track]');
  const prevButton = rail.querySelector('[data-rail-prev]');
  const nextButton = rail.querySelector('[data-rail-next]');
  if (!track || (!prevButton && !nextButton)) return;

  const scrollByAmount = () => track.clientWidth * 0.8;

  const updateButtonState = () => {
    if (prevButton) prevButton.disabled = track.scrollLeft <= 4;
    if (nextButton) {
      nextButton.disabled = track.scrollLeft + track.clientWidth >= track.scrollWidth - 4;
    }
  };

  prevButton?.addEventListener('click', () => {
    track.scrollBy({ left: -scrollByAmount(), behavior: 'smooth' });
  });
  nextButton?.addEventListener('click', () => {
    track.scrollBy({ left: scrollByAmount(), behavior: 'smooth' });
  });
  track.addEventListener('scroll', updateButtonState, { passive: true });
  updateButtonState();
});

/* -------------------------------------------------------------------- */
/* Wishlist (browser-local, feature-flagged)                             */
/* -------------------------------------------------------------------- */

const WISHLIST_KEY = 'corgi:wishlist';

function getWishlist() {
  try {
    return JSON.parse(localStorage.getItem(WISHLIST_KEY) || '[]');
  } catch (error) {
    return [];
  }
}

function setWishlist(ids) {
  try {
    localStorage.setItem(WISHLIST_KEY, JSON.stringify(ids));
  } catch (error) {
    /* localStorage unavailable — wishlist simply won't persist */
  }
}

function syncWishlistButtons() {
  const ids = getWishlist().map(String);
  document.querySelectorAll('[data-wishlist-toggle]').forEach((button) => {
    const isSaved = ids.includes(String(button.dataset.productId));
    button.setAttribute('aria-pressed', String(isSaved));
  });
}

document.addEventListener('click', (event) => {
  const button = event.target.closest('[data-wishlist-toggle]');
  if (!button) return;

  const id = String(button.dataset.productId);
  const ids = getWishlist().map(String);
  const index = ids.indexOf(id);

  if (index > -1) {
    ids.splice(index, 1);
  } else {
    ids.push(id);
  }

  setWishlist(ids);
  syncWishlistButtons();
});

syncWishlistButtons();

/* -------------------------------------------------------------------- */
/* Sort form auto-submit                                                 */
/* -------------------------------------------------------------------- */

document.querySelectorAll('[data-sort-select]').forEach((select) => {
  select.addEventListener('change', () => {
    select.closest('form')?.submit();
  });
});

/* -------------------------------------------------------------------- */
/* Product recommendations ("Complete the Look" fallback fetch)          */
/* -------------------------------------------------------------------- */

class ProductRecommendations extends HTMLElement {
  connectedCallback() {
    if (this.hasAttribute('data-curated')) return; // already server-rendered
    const url = this.dataset.url;
    if (!url || !window.fetch) return;

    fetch(url)
      .then((response) => response.text())
      .then((html) => {
        const wrapper = document.createElement('div');
        wrapper.innerHTML = html;
        const rendered = wrapper.querySelector('product-recommendations');
        if (rendered && rendered.innerHTML.trim()) {
          this.innerHTML = rendered.innerHTML;
        }
      })
      .catch(() => {
        /* leave the section empty — no curated data and fetch failed */
      });
  }
}

if (!customElements.get('product-recommendations')) {
  customElements.define('product-recommendations', ProductRecommendations);
}
